import {
    CapacitorSQLite,
    SQLiteConnection,
    SQLiteDBConnection
} from '@capacitor-community/sqlite';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { debugMode } from '../config';

export class Sqlite {
    private dbName: string
    private db: SQLiteDBConnection | null = null
    private sqlite: SQLiteConnection | null = null
    private version: number = 2
    constructor(dbname: string) {
        this.dbName = dbname
    }
    async open() {
        this.sqlite = new SQLiteConnection(CapacitorSQLite)
        const isSecretStored = await this.sqlite.isSecretStored()
        if (!isSecretStored) {
            await this.sqlite.setEncryptionSecret("I am mashfiq")
        }
        const ret = await this.sqlite.checkConnectionsConsistency();
        const isConn = (await this.sqlite.isConnection(this.dbName, false)).result;
        if (ret.result && isConn) {
            this.db = await this.sqlite.retrieveConnection(this.dbName, false)
        } else {
            this.db = await this.sqlite.createConnection(this.dbName, false, "no-encryption", this.version, false)
        }
        await this.db.open()
        this.getSQLiteVersion().then((version) => {
            if (version) {
                FirebaseCrashlytics.setCustomKey({
                    key: 'sqlite_version',
                    value: version,
                    type: 'string'
                })
            }
        })
    }
    async getSQLiteVersion(): Promise<string | null> {
        if (this.db === null) {
            return null
        }
        const result = await this.db.query("SELECT sqlite_version();");
        return result && result.values && result.values[0]['sqlite_version()']
            ? result.values[0]['sqlite_version()']
            : null;
    }
    async createCategoryTable() {
        let query = `CREATE TABLE IF NOT EXISTS post_categories (
          id INTEGER PRIMARY KEY NOT NULL,
          icon varchar(255) DEFAULT NULL,
          name varchar(255) DEFAULT NULL,
          parent_id int(10) DEFAULT NULL,
          status varchar(255) NOT NULL,
          package varchar(255) NOT NULL,
          keyword varchar(255) NULL,
          created_at timestamp NULL DEFAULT NULL,
          updated_at timestamp NULL DEFAULT NULL
        );`
        await this.execute(query)
        // Add an index on frequently queried columns
        await this.execute("CREATE INDEX IF NOT EXISTS idx_post_categories_status_keyword ON post_categories(status, keyword);");
    }
    async createPostTable() {
        let query = `CREATE TABLE IF NOT EXISTS posts (
          id INTEGER PRIMARY KEY NOT NULL,
          title varchar(255) DEFAULT NULL,
          content text,
          category varchar(255) NOT NULL,
          status varchar(255) NOT NULL,
          package varchar(255) NOT NULL,
          keyword varchar(255) NULL,
          created_at timestamp NULL DEFAULT NULL,
          updated_at timestamp NULL DEFAULT NULL
        );`
        await this.execute(query)
        await this.execute("CREATE INDEX IF NOT EXISTS idx_posts_status_keyword ON posts(status, keyword);");
    }
    insertFromJson(tableName: string, jsonData: any) {
        const colums = Object.keys(jsonData).join(", ")
        const dummyValues = [] as string[]
        Object.keys(jsonData).map(() => dummyValues.push("?"))
        const sql = 'INSERT INTO ' + tableName + ' (' + colums + ") VALUES (" + dummyValues.join(", ") + ");"
        // console.log("Goodbye "+sql)
        return this.run(sql, Object.values(jsonData))
    }
    updateFromJson(tableName: string, jsonData: any) {
        const dummyValues = [] as string[]
        const data = [] as any[]
        Object.keys(jsonData).filter((key) => (key !== "id")).map((key) => {
            dummyValues.push(key + " = ?")
            data.push(jsonData[key])
            return null
        })
        const sql = 'UPDATE ' + tableName + ' SET ' + dummyValues.join(", ") + " WHERE id = ? ;"
        data.push(jsonData.id)
        // console.log("Personal Log",sql)
        return this.run(sql, data)
    }
    async queryData(sql: string, value?: any[]) {
        // const start = new Date().getTime()
        await this.open()
        let res = await this.query(sql, value)

        await this.close()
        // console.log(`sql response: Time Taken : ${(new Date().getTime() - start)} ms`);
        return res && res.values ? res.values : []
    }
    async queryDataBatch(sql: string, value?: any[]) {
        // const start = new Date().getTime()
        let res = await this.query(sql, value)

        // console.log(`sql response: Time Taken : ${(new Date().getTime() - start)} ms`);
        return res && res.values ? res.values : []
    }
    async deleteTables() {
        let query = `
        DROP TABLE IF EXISTS posts;
        DROP TABLE IF EXISTS post_categories;
        DROP TABLE IF EXISTS migrations;
        `
        await this.execute(query)
    }
    async deletePostTable() {
        let query = `
        DROP TABLE IF EXISTS posts;
        `
        await this.execute(query)
    }
    async clean() {
        await this.open()
        await this.deleteTables()
        await this.close()
    }
    execute(query: string) {
        if (this.db) {
            return this.db.execute(query)
        } else {
            throw new Error("Database Not Initialized")
        }
    }
    run(query: string, values: any[] | undefined) {
        if (this.db) {
            return this.db.run(query, values)
        } else {
            throw new Error("Database Not Initialized")
        }
    }
    async count(tableName: string) {
        const result = await this.query(`SELECT count(*) count FROM ${tableName};`)
        return result && result.values ? result.values[0].count : 0
    }
    async categoryChildrenCount(catId: number): Promise<number> {
        const result = await this.query(`SELECT count(*) count FROM post_categories WHERE status = 'Published' AND parent_id = ?;`, [catId])
        return result && result.values ? result.values[0].count : 0
    }
    async ifItemExists(tableName: string, id: number) {
        const result = await this.query(`SELECT count(*) count FROM ${tableName} WHERE id = ?;`, [id])
        return (result && result.values && !!result.values[0].count)
    }
    async ifTableExists(tableName: string) {
        const result = await this.query(`SELECT count(*) count FROM sqlite_master WHERE type='table' AND name= ?;`, [tableName])
        return (result && result.values && !!result.values[0].count)
    }
    async getDt(tableName: string) {
        const result = await this.query("SELECT max(`updated_at`) dt FROM " + tableName + ";")
        return (result && result.values && result.values[0].dt)
    }
    async getLid(tableName: string) {     // Get Last ID
        const result = await this.query("SELECT max(`id`) lid FROM " + tableName + ";")
        return (result && result.values && result.values[0].lid)
    }
    async getIds(tableName: string) {     // Get All IDs
        const result = await this.query("SELECT DISTINCT `id` FROM " + tableName + ";")
        if (result && result.values) {
            return result.values.map((item) => (item.id))
        } else {
            return []
        }
    }
    query(query: string, values: any[] | undefined = []) {
        if (this.db) {
            return this.db.query(query, values)
        } else {
            // console.log("db is not connected")
        }
    }
    async updateCountsForCategories(catIds: number[]) {
        if (!this.db) {
            throw new Error("Database Not Initialized");
        }
        if (catIds.length < 1) {
            return
        }
        const start = (new Date()).getTime();
        // Combined Query
        const query = `
            UPDATE post_categories
            SET
                children_count = (
                    SELECT COUNT(*)
                    FROM post_categories c
                    WHERE c.parent_id = post_categories.id
                ),
                posts_count = (
                    SELECT COUNT(*)
                    FROM posts p
                    WHERE p.category = post_categories.id
                )
            WHERE id in (${catIds.join(",")});
        `;
        const result = await this.db.execute(query);

        const end = (new Date()).getTime();
        const timeElapsedInSec = (end - start) / 1000
        const rowsEffected = result.changes?.changes
        debugMode && console.log("updateCountsForCategories", { catIds, timeElapsedInSec, rowsEffected });
    }

    async updateCountsInDB() {
        if (!this.db) {
            throw new Error("Database Not Initialized")
        }
        const start = (new Date()).getTime()
        // Add the two columns to the `post_categories` table
        const children_count_row = await this.db.execute(`UPDATE post_categories` +
            ` SET children_count = (` +
            `    SELECT COUNT(*)` +
            `    FROM post_categories c` +
            `    WHERE c.parent_id = post_categories.id` +
            ` ) WHERE id IN (SELECT DISTINCT parent_id FROM post_categories d WHERE d.parent_id is not null );`);
        const posts_count_row = await this.db.execute(`UPDATE post_categories` +
            ` SET posts_count = (` +
            `    SELECT COUNT(*)` +
            `    FROM posts p` +
            `    WHERE p.category = post_categories.id` +
            ` ) WHERE id IN (SELECT DISTINCT category FROM posts d WHERE d.category is not null );`);
        const end = (new Date()).getTime()
        const childrenRowsEffected = children_count_row.changes?.changes
        const postRowsEffected = posts_count_row.changes?.changes
        const timeElapsedInSec = (end - start) / 1000
        debugMode && console.log("updateCountsInDB", { timeElapsedInSec ,childrenRowsEffected,postRowsEffected})
    }
    async close() {
        if (this.db) {
            await this.db.close();
        }
        if (this.sqlite) {
            await this.sqlite.closeConnection(this.dbName, false);
        }
    }
}

export const database = new Sqlite("doctor_rx")

